import * as React from 'react';
import {
  Box,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
  Grid2 as Grid,
  Card,
  CardContent
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { cheapestTariffsColumns } from '../internals/data/gridData';
import LoadingData from './LoadingData';
import HorizontalScrollIndicator from './HorizontalScrollIndicator';
import ApiClient from '../../utils/apiClient';
import {
  getCachedData,
  setDailyCachedData,
  getDailyCacheKey,
  createCacheKeyFromParams
} from '../../utils/cacheUtils';

interface TariffData {
  display_name: string;
  unit_cost_inc_vat: number;
  standing_cost_inc_vat: number;
  total_cost_inc_vat: number;
  is_current_tariff: number;
  product_id?: number;
}

type CalculatedTariffData = TariffData & {
    combined_cost_inc_vat: number;
};

type CachedTariffPayload = {
  tariffs: CalculatedTariffData[];
  analysisStartDate: string;
  analysisEndDate: string;
};

interface TariffGridData {
  rank: number;
  display_name: string;
  unit_cost_inc_vat: number;
  standing_cost_inc_vat: number;
  total_cost_inc_vat: number;
  product_id?: number;
}

interface CheapestTariffsProps {
  days: number;
  consumptionTypeId: number;
  paymentMethodId?: number;
}

const getRowId = (row: TariffGridData) => `${row.display_name}_${row.product_id}`;

const formatAnalysisDate = (isoDate: string) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

export default function CheapestTariffs({
  days,
  consumptionTypeId,
  paymentMethodId = 1
}: CheapestTariffsProps) {
  const theme = useTheme();
  const [tariffData, setTariffData] = React.useState<CalculatedTariffData[]>([]);
  const [analysisStartDate, setAnalysisStartDate] = React.useState<string | null>(null);
  const [analysisEndDate, setAnalysisEndDate] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  // Cache key generator - uses parameters and current date
  const getCacheKey = (days: number, consumptionTypeId: number, paymentMethodId: number): string => {
    const params = new URLSearchParams({
      days: days.toString(),
      consumption_type_id: consumptionTypeId.toString(),
      payment_method_id: paymentMethodId.toString()
    });
    const { keyBase } = createCacheKeyFromParams('tariff-data', params);
    // Use refreshHour 0 to refresh at midnight (new day)
    return getDailyCacheKey(keyBase, 0);
  };

  React.useEffect(() => {
    const fetchRawTariffData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check cache first — empty results are not cached, so retry on refresh
        const cacheKey = getCacheKey(days, consumptionTypeId, paymentMethodId);
        const cachedPayload = getCachedData<CachedTariffPayload>(cacheKey);
        let calculatedTariffs =
          cachedPayload && cachedPayload.tariffs.length > 0 ? cachedPayload.tariffs : null;
        let startDate = cachedPayload?.analysisStartDate ?? null;
        let endDate = cachedPayload?.analysisEndDate ?? null;
        
        if (!calculatedTariffs) {
          // Cache miss - fetch from API
          const params = new URLSearchParams({
            days: days.toString(),
            consumption_type_id: consumptionTypeId.toString(),
            payment_method_id: paymentMethodId.toString()
          });

          const data = await ApiClient.get(`api/products/raw-tariff-data?${params}`);

          if (!data.success) {
            setError(data.msg || 'Failed to fetch tariff data');
            setLoading(false);
            return;
          }

          startDate = data.analysis_start_date ?? null;
          endDate = data.analysis_end_date ?? null;

          calculatedTariffs = ((data.data ?? []) as TariffData[])
          .map(x => ({
              ...x,
              combined_cost_inc_vat: x.standing_cost_inc_vat + x.total_cost_inc_vat
          }))
          .sort((a, b) => a.combined_cost_inc_vat - b.combined_cost_inc_vat);
          
          if (calculatedTariffs.length > 0 && startDate && endDate) {
            const { prefix } = createCacheKeyFromParams('tariff-data', params);
            setDailyCachedData(
              cacheKey,
              { tariffs: calculatedTariffs, analysisStartDate: startDate, analysisEndDate: endDate },
              prefix,
              0
            );
          }
        }

        setAnalysisStartDate(startDate);
        setAnalysisEndDate(endDate);
        setTariffData(calculatedTariffs ?? []);
      } catch (err) {
        setError('Error fetching tariff data');
        console.error('Error fetching raw tariff data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRawTariffData();
  }, [days, consumptionTypeId, paymentMethodId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const analysisPeriodText =
    analysisStartDate && analysisEndDate
      ? `Calculated from your actual electricity consumption between ${formatAnalysisDate(analysisStartDate)} and ${formatAnalysisDate(analysisEndDate)}.`
      : null;

  const analysisPeriodSubtitle = analysisPeriodText ? (
    <Typography variant="body1" sx={{ mb: 2, px: { xs: 1, md: 0 } }}>
      {analysisPeriodText}
    </Typography>
  ) : null;

  if (error) {
    return (
      <Box>
        {analysisPeriodSubtitle}
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  if (tariffData.length === 0) {
    return (
      <Box>
        {analysisPeriodSubtitle}
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>No Data</AlertTitle>
          No tariff data available for the selected period.
        </Alert>
      </Box>
    );
  }

  // Find current and cheapest tariffs
  const cheapestTariff = tariffData[0]; // Data is sorted by total_cost_inc_vat ascending
  const remainingTariffs = tariffData.slice(1); // Entries 2 to n

  return (
    <Box sx={{ mb: 3 }}>
      {analysisPeriodSubtitle}
      {cheapestTariff && (
        <Card sx={{ 
          mb: 3,
          mx: { xs: 1, md: 0 },
          backgroundColor: theme.palette.background.paper
        }}>
          <CardContent>
            {/* Mobile Layout: Stacked */}
            <Box sx={{ 
              display: { xs: 'block', sm: 'block', md: 'none' }
            }}>
              {/* Tariff Name - Full Width */}
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Typography variant="h2">
                  #1 {cheapestTariff.display_name}
                </Typography>
              </Box>
              
              {/* Cost Items - Row Below */}
              <Grid container spacing={2}>
                <Grid size={4}>
                  <Typography variant="h5" sx={{ textAlign: 'center' }}>
                    Consumption
                  </Typography>
                  <Typography variant="h2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    £{cheapestTariff.total_cost_inc_vat.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="h5" sx={{ textAlign: 'center' }}>
                    Standing Charges
                  </Typography>
                  <Typography variant="h2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    £{cheapestTariff.standing_cost_inc_vat.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="h5" sx={{ textAlign: 'center' }}>
                    Total Cost
                  </Typography>
                  <Typography variant="h2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    £{cheapestTariff.combined_cost_inc_vat.toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Desktop Layout: Side by Side */}
            <Box sx={{ 
              display: { xs: 'none', sm: 'none', md: 'flex' }, 
              alignItems: 'center', 
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h2">
                  #1 {cheapestTariff.display_name}
                </Typography>
              </Box>
              
              <Grid container spacing={2} sx={{ minWidth: 500 }}>
                <Grid size={4}>
                  <Typography variant="h5">
                    Consumption
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    £{cheapestTariff.total_cost_inc_vat.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="h5">
                    Standing Charges
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    £{cheapestTariff.standing_cost_inc_vat.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="h5">
                    Total Cost
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    £{cheapestTariff.combined_cost_inc_vat.toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Data Grid for remaining tariffs */}
      {remainingTariffs.length > 0 && (
        <Box sx={{ width: '100%', position: 'relative' }}>
          <LoadingData open={loading} />
          <DataGrid
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "normal",
              lineHeight: "normal"
            },
            "& .MuiDataGrid-columnHeader": {
              height: "unset !important"
            },
            "& .MuiDataGrid-columnHeaders": {
              maxHeight: "168px !important"
            },
            "& .MuiDataGrid-row": {
              "&:nth-of-type(even)": {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
              },
              "&:nth-of-type(odd)": {
                backgroundColor: 'transparent',
              },
              "&:hover": {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              },
              "&.current-tariff": {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                border: `2px solid ${theme.palette.primary.main}`,
                "&:hover": {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.3)' : 'rgba(25, 118, 210, 0.15)',
                }
              }
            },
            position: 'relative',
            // Remove border radius on mobile
            [theme.breakpoints.down('md')]: {
              borderRadius: 0,
              '& .MuiDataGrid-main': {
                borderRadius: 0,
              },
              '& .MuiDataGrid-container--top': {
                borderRadius: 0,
              },
              '& .MuiDataGrid-container--bottom': {
                borderRadius: 0,
              },
              '& .MuiDataGrid-columnHeaders': {
                borderRadius: 0,
              },
              '& .MuiDataGrid-footerContainer': {
                borderRadius: 0,
              },
            },
          }}
          autoHeight
          rows={remainingTariffs.map((row, index) => {
            const { is_current_tariff, ...rowWithoutCurrentTariff } = row;
            return {
              ...rowWithoutCurrentTariff,
              rank: index + 2 // Start ranking from 2
            };
          })}
          columns={cheapestTariffsColumns}
          rowHeight={40}
          getRowId={getRowId}
          getRowClassName={(params) => {
            // Find the original row data to check is_current_tariff
            const originalRow = remainingTariffs.find(row => 
              `${row.display_name}_${row.product_id}` === params.id
            );
            if (originalRow?.is_current_tariff === 1) {
              return 'current-tariff';
            }
            return params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
          }}
          hideFooter
          disableColumnResize
          density="compact"
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          slotProps={{
            filterPanel: {
              filterFormProps: {
                logicOperatorInputProps: {
                  variant: 'outlined',
                  size: 'small',
                },
                columnInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                operatorInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                valueInputProps: {
                  InputComponentProps: {
                    variant: 'outlined',
                    size: 'small',
                  },
                },
              },
            },
          }}
          />
          <HorizontalScrollIndicator />
        </Box>
      )}
    </Box>
  );
}
